import { CommonModule } from "@angular/common";
import { Component, HostListener, Input, OnInit } from "@angular/core";
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Client, EntityWithTemplate, JobBoardPageComponent, JobMetaResponse } from "@interfaces";
import { Feature as GeoJSONFeature } from "geojson";
import { Observable, debounceTime, distinctUntilChanged, filter, switchMap } from "rxjs";
import { GtagService } from "src/app/containers/gtag/gtag.service";
import { GeocodeService } from "src/app/rest/geocode.service";
import { JobsSearchMetaService } from "src/app/rest/jobs-search-meta.service";
import { SubdomainService } from "src/app/rest/subdomain.service";

@Component({
    selector: "app-search-box",
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule],
    templateUrl: "./search-box.component.html",
})
export class SearchBoxComponent implements OnInit {
    @Input() entity: EntityWithTemplate;
    @Input() component: JobBoardPageComponent;
    public places$: Observable<GeoJSONFeature[]>;
    public searchMeta: JobMetaResponse;
    public searchForm: UntypedFormGroup;
    public client: Client;
    public geoData = {};
    public autocompleteFocus = false;


    public readonly seniorityTypes = [
        {
            value: "JOB_LEVEL_UNSPECIFIED",
            label: "",
        },
        {
            value: "ENTRY_LEVEL",
            label: "Trainee",
        },
        {
            value: "EXPERIENCED",
            label: "Experienced",
        },
        {
            value: "MANAGER",
            label: "Management",
        },
        {
            value: "DIRECTOR",
            label: "Director",
        },
        {
            value: "EXECUTIVE",
            label: "Executive",
        },
    ];

    constructor(
        private fb: UntypedFormBuilder,
        private route: ActivatedRoute,
        private gtag: GtagService,
        private subdomain: SubdomainService,
        private meta: JobsSearchMetaService,
        private router: Router,
        private geoService: GeocodeService
    ) {
        this.searchForm = this.fb.group({
            keywords: [],
            hashtags: [[]],
            reference: [],
            category: "",
            locationLabel: "",
            seniorityType: "",
            nestedLocationSearch: [],
            businessUnit: [],
            locationGeo: [],
            locationGeoSearchInput: [],
            distance: [5],
            lang: [],
        });
    }

    async ngOnInit(): Promise<void> {
        await this.fetchMetaData();
        this.setupForm();
        this.subdomain.getClient().subscribe((client) => {
            this.client = client;
        });
        this.places$ = this.searchForm.get("locationGeoSearchInput")!.valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(1000),
            filter((name) => !!name),
            switchMap((name) => this.geoService.geocode(name))
        );
    }

    public async fetchMetaData(): Promise<any> {
        const client = await this.subdomain.getClient().toPromise();
        const params = {
            employer: client._id,
        };
        this.searchMeta = await (this.meta.find(params).toPromise() as Promise<JobMetaResponse>);
    }

    private setupForm() {
        const categorySlug = this.route.snapshot?.params?.category || this.route.firstChild?.snapshot?.params?.category;
        // Lock filters set in editor.
        if (this.component?.filterJobs?.category || categorySlug) {
            this.searchForm.get("category").disable();
        }

        if (this.component?.filterJobs?.location) {
            this.searchForm.get("locationLabel").disable();
        }

        if (this.component?.filterJobs?.seniority) {
            this.searchForm.get("seniorityType").disable();
        }

        if (this.component?.filterJobs?.hashtags) {
            this.searchForm.get("hashtags").disable();
        }

        if (this.component?.filterJobs?.businessUnit) {
            this.searchForm.get("businessUnit").disable();
        }

        // Apply filters from editor or query params.

        if(this.component?.filterJobs?.lang) {
            this.searchForm.get("lang").setValue(this.component.filterJobs.lang);
        }

        if (this.route.snapshot.queryParams?.keywords) {
            this.searchForm.get("keywords").setValue(this.route.snapshot.queryParams.keywords);
        }

        if (this.route.snapshot.queryParams?.locationGeoSearchInput && this.route.snapshot.queryParams?.locationGeo) {
            this.searchForm.get("locationGeo").setValue(this.route.snapshot.queryParams?.locationGeo);
            this.searchForm
                .get("locationGeoSearchInput")
                .setValue(this.route.snapshot.queryParams?.locationGeoSearchInput);
        }

        if (this.route.snapshot.queryParams?.distance) {
            this.searchForm.get("distance").setValue(parseInt(this.route.snapshot.queryParams?.distance));
        }

        if (this.route.snapshot.queryParams?.hashtags?.length) {
            this.searchForm.get("hashtags").setValue(this.route.snapshot.queryParams.hashtags);
        } else if (this.component.filterJobs?.hashtags?.length) {
            this.searchForm.get("hashtags").setValue(this.component.filterJobs.hashtags);
        }

        if (this.route.snapshot.queryParams?.category) {
            this.searchForm.get("category").setValue(this.route.snapshot.queryParams.category);
        } else if (this.component.filterJobs?.category) {
            this.searchForm.get("category").setValue(this.component.filterJobs.category);
        }

        if (this.route.snapshot.queryParams?.locationLabel) {
            this.searchForm.get("locationLabel").setValue(this.route.snapshot.queryParams.locationLabel);
        } else if (this.component.filterJobs?.location) {
            this.searchForm.get("locationLabel").setValue(this.component.filterJobs.location);
        }

        if (this.searchMeta?.greenhouseLocations) {
            this.searchForm.get("nestedLocationSearch").setValue(true);
        }

        if (this.route.snapshot.queryParams?.businessUnit) {
            this.searchForm.get("businessUnit").setValue(this.route.snapshot.queryParams.businessUnit);
        } else if (this.component.filterJobs?.businessUnit) {
            this.searchForm.get("businessUnit").setValue(this.component.filterJobs.businessUnit);
        }
    }

    @HostListener("document:keydown.enter", ["$event"])
    public onEnter(event: KeyboardEvent) {
        this.emitSearch();
    }

    public emitSearch() {
        this.gtag.fireSearchEvent(this.searchForm.value);
        this.search();
    }

    public search() {
        let redirectTo = [];
        if (this.component.hideJobs) {
            redirectTo = [this.component.redirectToPage] ?? [];
        }
        const payload = this.searchForm.getRawValue();
        this.router.navigate(redirectTo, { relativeTo: this.route, queryParams: payload });
    }

    public selectResult(event: Event, result: GeoJSONFeature) {
        event.preventDefault();
        this.searchForm
                .get("locationGeo")
                .setValue([result.geometry?.['coordinates'][0], result.geometry?.['coordinates'][1]]);
        this.searchForm.get("locationGeoSearchInput").setValue(result.properties?.formatted);
        this.autocompleteFocus = false;
    }

    public clearSearch() {
        this.searchForm.reset();
    }
}
